import { observablePersistIndexedDB } from "@legendapp/state/persist-plugins/indexeddb";
import { configureSynced } from "@legendapp/state/sync";
import { syncedCrud } from "@legendapp/state/sync-plugins/crud";
import { toast } from "sonner";

import { logger } from "./logger";

/**
 * tRPC surfaces input-validation failures with `error.message` set to Zod's
 * stringified issue array (`[{ code, path, message }, ...]`). Dumping that JSON
 * into a toast is unreadable, so detect it and fall back to a friendly message.
 * Returns `null` when the message is not a Zod issue array, so genuine error
 * text (e.g. a 403 reason) passes through unchanged.
 */
const friendlyZodMessage = (message: string): string | null => {
  const trimmed = message.trim();
  if (!trimmed.startsWith("[")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    const isZodIssues =
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed.every(
        (issue) =>
          issue != null &&
          typeof issue === "object" &&
          "code" in issue &&
          "path" in issue,
      );
    return isZodIssues ? "Couldn't save changes — please try again." : null;
  } catch {
    return null;
  }
};

export const syncedCrudPersist = configureSynced(syncedCrud, {
  // Replace the existing items with the server items
  mode: "set",
  debounceSet: 400,
  updatePartial: true,
  // Wire-only tombstone marker. When a remote `update({ value })` carries
  // `_deleted: true`, Legend State drops the entry from the observable map
  // without re-firing the local CRUD `delete` callback (so no echo loop).
  // Server `*.router.ts` `delete` mutations broadcast `{ id, _deleted: true }`
  // after the DB delete; see `apps/api/src/trpc/routers/*.router.ts`.
  fieldDeleted: "_deleted",
  persist: {
    plugin: observablePersistIndexedDB({
      databaseName: "index",
      // Bumped to 4 to create the `dataset-folders` store: `onupgradeneeded`
      // only runs on a version change, so a new entry in `tableNames` alone
      // would never exist on a client already at the previous version.
      version: 4,
      // Reset of cached rows whose shape has since changed (stale
      // currency/number column configs, and now the run metadata — `folderId`,
      // `rowCount`, `sizeBytes` — a persisted dataset predates). A version bump
      // alone does NOT clear data: the plugin's `onupgradeneeded` only creates
      // missing stores. Listing a table here `deleteObjectStore`s it on the
      // upgrade; `tableNames` then recreates it empty, so `syncedCrud`
      // re-downloads from the server. OPFS Arrow cache is untouched.
      deleteTableNames: ["datasets", "blocks"],
      tableNames: [
        // Dictionary tables
        "board-filters",
        "board-insights",
        "board-invitations",
        "board-members",
        "blocks",
        "boards",
        "catalog",
        "connections",
        "dataset-folders",
        "datasources",
        "datasets",
        "catalogs",
        "organization-invitations",
        "organizations",
      ],
    }),
  },
  onError: (error) => {
    logger.error(error);
    // Surface sync failures to the user in every environment. Without this a
    // rejected CRUD mutation (e.g. a 403) would revert optimistically with no
    // feedback, leaving the user thinking the change stuck when it didn't.
    // Raw Zod issue arrays are unreadable, so swap them for a friendly message
    // (full detail is preserved in the logged error above).
    toast.error(friendlyZodMessage(error.message) ?? error.message);
  },
});
